import classes from "./ServiceContent.module.scss";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { CustomTextField } from "../../../CustomTextField/CustomTextField";
import { Button } from "../../../Button/Button";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import CustomRadio from "../../../CustomRadio/CustomRadio";
import { TimePickerCustom } from "../../../TimePickerCustom/TimePickerCustom";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { rulesLetterLang } from "../../../../../schemas/ModalServiceSchema";
import {
  ModalContentProps,
  QueueItem,
  ServicesTableType,
} from "../../../../../types/Admin/CategoriesTopicsTypes";
import { useCreateTopic } from "../../../../../hooks/Admin/CategoriesAndServisesTable/useCreateTopic";
import { useEditTopic } from "../../../../../hooks/Admin/CategoriesAndServisesTable/useEditTopic";
import { useGetAllCategoriesTopics } from "../../../../../hooks/Admin/CategoriesAndServisesTable/useGetAllCategoriesTopics";
import BranchQueueTreeSelect from "../../../BranchQueueTreeSelect/BranchQueueTreeSelect";
import { ModalMode, TimeMode } from "../../../../../types/enums";
import modalBase from "../../../../ModalBase/ModalBase.module.scss";
import classNames from "classnames";
import { useAdminQueueIdsFilter } from "../../../../../hooks/Admin/useAdminQueueIdsFilter";
import { ItemIconEditor } from "../../../ItemIconEditor/ItemIconEditor";
import { ItemIconSettings } from "../../../../../types/Admin/ItemIconTypes";
import { defaultItemIconSettings } from "../../../../../utils/mediaBackground";
import { mapItemIconFields } from "../../../../../utils/Admin/CategoriesAndServicesTable/mapItemIconFields";
import { mapChildrenIconFields } from "../../../../../utils/Admin/CategoriesAndServicesTable/mapChildrenIconFields";
import { mapExtraInputFields, defaultExtraInputFields, ExtraInputFields } from "../../../../../utils/Admin/CategoriesAndServicesTable/mapExtraInputFields";
import { resolveIconSettingsForEdit } from "../../../../../utils/Admin/CategoriesAndServicesTable/resolveIconSettingsForEdit";
import { resolveChildrenIconSettingsForEdit } from "../../../../../utils/Admin/CategoriesAndServicesTable/resolveChildrenIconSettingsForEdit";
import { resolveBindingsForEdit } from "../../../../../utils/Admin/CategoriesAndServicesTable/resolveBindingsForEdit";
import { useUploadTopicIcon } from "../../../../../hooks/Admin/CategoriesAndServisesTable/useUploadTopicIcon";
import { useUploadTopicChildrenIcon } from "../../../../../hooks/Admin/CategoriesAndServisesTable/useUploadTopicChildrenIcon";

export const ServiceContent: FC<ModalContentProps> = ({
  selectedRadioValue,
  handleRadioChange,
  setActiveModal,
  modalDataFromTable,
  modalMode,
}) => {
  const sheduleData =
    modalMode === ModalMode.Edit
      ? modalDataFromTable.startFinishWork.split("-")
      : ["10:00", "18:00"];

  const [startTime, setStartTime] = useState(sheduleData[0]);
  const [endTime, setEndTime] = useState(sheduleData[1]);
  const [selectedQueueIds, setSelectedQueueIds] = useState<number[]>([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
  const [isBindingTouched, setIsBindingTouched] = useState(false);
  const [iconSettings, setIconSettings] = useState<ItemIconSettings>(
    defaultItemIconSettings,
  );
  const [childrenIconSettings, setChildrenIconSettings] =
    useState<ItemIconSettings>(defaultItemIconSettings);
  const [extraInputSettings, setExtraInputSettings] = useState<ExtraInputFields>(
    defaultExtraInputFields,
  );
  const [extraInputLimitLength, setExtraInputLimitLength] = useState(false);
  const [extraInputError, setExtraInputError] = useState<string | null>(null);
  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const { uploadTopicIcon, isUploadTopicIconLoading } = useUploadTopicIcon();
  const { uploadTopicChildrenIcon, isUploadTopicChildrenIconLoading } =
    useUploadTopicChildrenIcon();

  const { allCategoriesTopicsResp, isAllCategoriesTopicsReceived } =
    useGetAllCategoriesTopics();

  function getQueuesToCheck(): number[] {
    if (selectedQueueIds.length > 0) {
      return selectedQueueIds;
    }
    return modalDataFromTable.queues?.map((queue) => queue.id) ?? [];
  }

  function hasSharedQueue(
    serviceQueues: QueueItem[] | undefined,
    queuesToCheck: number[],
  ): boolean {
    const serviceQueueIds = serviceQueues?.map((queue) => queue.id) ?? [];
    return serviceQueueIds.some((queueId) => queuesToCheck.includes(queueId));
  }

  function checkIsUniqueInQueues(
    value: string | undefined,
    getExistingValue: (obj: ServicesTableType) => string | undefined,
  ): boolean {
    if (!isAllCategoriesTopicsReceived || !value?.trim()) {
      return true;
    }

    const normalizedValue = value.trim().toLowerCase();
    const queuesToCheck = getQueuesToCheck();
    if (queuesToCheck.length === 0) {
      return true;
    }

    return !allCategoriesTopicsResp.services.some((obj: ServicesTableType) => {
      if (modalMode === ModalMode.Edit && obj.id === modalDataFromTable.id) {
        return false;
      }

      const existingValue = getExistingValue(obj);
      if (typeof existingValue !== "string" || !existingValue.trim()) {
        return false;
      }

      return (
        existingValue.trim().toLowerCase() === normalizedValue &&
        hasSharedQueue(obj.queues, queuesToCheck)
      );
    });
  }

  function checkIsNameUnique(
    nameField: string,
    value: string | undefined,
  ): boolean {
    return checkIsUniqueInQueues(
      value,
      (obj) => obj[nameField as keyof ServicesTableType] as string | undefined,
    );
  }

  function checkIsLetterUnique(value: string | undefined): boolean {
    return checkIsUniqueInQueues(value, (obj) => obj.itemNamePrefix);
  }

  const ModalServiceSchema = useMemo(
    () =>
      yup.object({
        inputTextRu: yup
          .string()
          .required("Необходимо заполнить поле")
          .test(
            "unique-nameRu",
            "Такое наименование уже существует в выбранной очереди",
            (value) => checkIsNameUnique("nameRu", value),
          )
          .test("no-empty-spaces", "Введите текст", (value) => {
            return !/^\s+$/.test(value);
          }),
        inputTextEn: yup
          .string()
          .required("Необходимо заполнить поле")
          .test(
            "unique-nameEn",
            "Такое наименование уже существует в выбранной очереди",
            (value) => checkIsNameUnique("nameEn", value),
          )
          .test("no-empty-spaces", "Введите текст", (value) => {
            return !/^\s+$/.test(value);
          }),
        inputTextBy: yup
          .string()
          .required("Необходимо заполнить поле")
          .test(
            "unique-nameBe",
            "Такое наименование уже существует в выбранной очереди",
            (value) => checkIsNameUnique("nameBe", value),
          )
          .test("no-empty-spaces", "Введите текст", (value) => {
            return !/^\s+$/.test(value);
          }),
        letter: yup
          .string()
          .required("Необходимо заполнить поле")
          .matches(rulesLetterLang, {
            message: "Введите букву верно",
            excludeEmptyString: true,
          })
          .test(
            "unique-letter",
            "Такая буква уже существует в выбранной очереди",
            (value) => checkIsLetterUnique(value),
          )
          .test("no-empty-spaces", "Введите текст", (value) => {
            return !/^\s+$/.test(value);
          })
          .max(1, "Превышен лимит символов"),
      }),
    [
      isAllCategoriesTopicsReceived,
      allCategoriesTopicsResp,
      selectedQueueIds,
      modalMode,
      modalDataFromTable.id,
      modalDataFromTable.queues,
    ],
  );

  type FormData = yup.InferType<typeof ModalServiceSchema>;
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    reset,
    trigger,
    setError,
    formState: { errors, isSubmitted },
  } = useForm<FormData>({
    resolver: yupResolver(ModalServiceSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      inputTextRu: "",
      inputTextEn: "",
      inputTextBy: "",
      letter: "",
    },
  });

  const { createTopic, isLoadingCreatedTopic } = useCreateTopic(() =>
    setActiveModal(false),
  );
  const { editTopic, isLoadingTopicEditing } = useEditTopic(
    setActiveModal,
    modalDataFromTable.id,
  );
  const initializedForIdRef = useRef<number | null>(null);
  const iconLoadedForIdRef = useRef<number | null>(null);
  const selectedQueueIdsRef = useRef<number[]>([]);
  const selectedBranchIdsRef = useRef<number[]>([]);

  const applyBindings = (branchIds: number[], queueIds: number[]) => {
    selectedBranchIdsRef.current = branchIds;
    selectedQueueIdsRef.current = queueIds;
    if (branchIds.length > 0) {
      setSelectedBranchIds(branchIds);
    }
    if (queueIds.length > 0) {
      setSelectedQueueIds(queueIds);
    }
  };

  useEffect(() => {
    selectedQueueIdsRef.current = selectedQueueIds;
  }, [selectedQueueIds]);

  useEffect(() => {
    selectedBranchIdsRef.current = selectedBranchIds;
  }, [selectedBranchIds]);

  useEffect(() => {
    if (!isSubmitted) return;
    void trigger(["inputTextRu", "inputTextEn", "inputTextBy", "letter"]);
  }, [selectedQueueIds, trigger, isSubmitted]);

  useEffect(() => {
    if (modalMode !== ModalMode.Edit || !modalDataFromTable?.startFinishWork) {
      return;
    }

    const entityId = modalDataFromTable.id;
    const isNewEntity = initializedForIdRef.current !== entityId;

    if (isNewEntity) {
      initializedForIdRef.current = entityId;
      iconLoadedForIdRef.current = null;
      setValue("inputTextRu", modalDataFromTable.nameRu);
      setValue("inputTextEn", modalDataFromTable.nameEn);
      setValue("inputTextBy", modalDataFromTable.nameBe);
      setValue("letter", modalDataFromTable.itemNamePrefix);
      const extraFields = mapExtraInputFields(modalDataFromTable);
      setExtraInputSettings(extraFields);
      setExtraInputLimitLength(
        extraFields.extraInputMinLength != null ||
          extraFields.extraInputMaxLength != null,
      );
      setExtraInputError(null);
      const timeArr = modalDataFromTable.startFinishWork.split("-");
      setStartTime(timeArr[0]);
      setEndTime(timeArr[1]);
      setIsBindingTouched(false);
    }

    if (!isBindingTouched) {
      const bindings = resolveBindingsForEdit(
        modalDataFromTable as ServicesTableType,
        allCategoriesTopicsResp,
        "service",
      );
      applyBindings(bindings.branchIds, bindings.queueIds);
    }
  }, [
    modalDataFromTable?.id,
    modalDataFromTable?.startFinishWork,
    modalMode,
    allCategoriesTopicsResp,
    isBindingTouched,
  ]);

  useEffect(() => {
    if (modalMode === ModalMode.Edit) {
      return;
    }
    initializedForIdRef.current = null;
    iconLoadedForIdRef.current = null;
    handleResetForm();
  }, [modalMode]);

  useEffect(() => {
    if (
      modalMode !== ModalMode.Edit ||
      !modalDataFromTable?.id ||
      !allCategoriesTopicsResp
    ) {
      return;
    }

    if (iconLoadedForIdRef.current === modalDataFromTable.id) {
      return;
    }

    iconLoadedForIdRef.current = modalDataFromTable.id;
    const data = modalDataFromTable as ServicesTableType;
    setIconSettings(
      resolveIconSettingsForEdit(data, allCategoriesTopicsResp, "service"),
    );
    setChildrenIconSettings(
      resolveChildrenIconSettingsForEdit(
        data,
        allCategoriesTopicsResp,
        "service",
      ),
    );
  }, [modalDataFromTable?.id, modalMode, allCategoriesTopicsResp]);

  function handleResetForm() {
    reset();
    selectedQueueIdsRef.current = [];
    selectedBranchIdsRef.current = [];
    setSelectedQueueIds([]);
    setSelectedBranchIds([]);
    setIconSettings(defaultItemIconSettings);
    setChildrenIconSettings(defaultItemIconSettings);
    setExtraInputSettings(defaultExtraInputFields);
    setExtraInputLimitLength(false);
    setExtraInputError(null);
    setIsBindingTouched(false);
  }

  function checkStartTime() {
    return startTime === null;
  }

  function checkEndTime() {
    return endTime === null;
  }

  function validateUniqueFields(data: FormData): boolean {
    const uniqueChecks: Array<{
      field: keyof FormData;
      message: string;
      isValid: boolean;
    }> = [
      {
        field: "inputTextRu",
        message: "Такое наименование уже существует в выбранной очереди",
        isValid: checkIsNameUnique("nameRu", data.inputTextRu),
      },
      {
        field: "inputTextEn",
        message: "Такое наименование уже существует в выбранной очереди",
        isValid: checkIsNameUnique("nameEn", data.inputTextEn),
      },
      {
        field: "inputTextBy",
        message: "Такое наименование уже существует в выбранной очереди",
        isValid: checkIsNameUnique("nameBe", data.inputTextBy),
      },
      {
        field: "letter",
        message: "Такая буква уже существует в выбранной очереди",
        isValid: checkIsLetterUnique(data.letter),
      },
    ];

    let isValid = true;
    for (const check of uniqueChecks) {
      if (!check.isValid) {
        setError(check.field, { type: "manual", message: check.message });
        isValid = false;
      }
    }

    return isValid;
  }

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    const { inputTextRu, inputTextEn, inputTextBy, letter } = data;
    const branchIds = [...selectedBranchIdsRef.current];
    const queueIds = [...selectedQueueIdsRef.current];
    const isBindingValid = branchIds.length > 0 && queueIds.length > 0;
    if (!isBindingValid) {
      setIsBindingTouched(true);
      return;
    }
    if (!validateUniqueFields(data)) {
      return;
    }
    if (extraInputSettings.extraInputEnabled) {
      const description = extraInputSettings.extraInputDescription?.trim() ?? "";
      if (!description) {
        setExtraInputError("Необходимо заполнить описание");
        return;
      }
      if (extraInputLimitLength) {
        const minLength = Number(extraInputSettings.extraInputMinLength);
        const maxLength = Number(extraInputSettings.extraInputMaxLength);
        if (!Number.isInteger(minLength) || !Number.isInteger(maxLength)) {
          setExtraInputError("Укажите минимальную и максимальную длину");
          return;
        }
        if (minLength < 1 || maxLength < 1 || minLength > 128 || maxLength > 128) {
          setExtraInputError("Длина должна быть от 1 до 128");
          return;
        }
        if (minLength > maxLength) {
          setExtraInputError("Минимальная длина не может быть больше максимальной");
          return;
        }
      }
    }
    setExtraInputError(null);
    const extraPayload = {
      extraInputEnabled: Boolean(extraInputSettings.extraInputEnabled),
      extraInputDescription:
        extraInputSettings.extraInputDescription?.trim() || null,
      extraInputPrint: Boolean(extraInputSettings.extraInputPrint),
      extraInputRequired: Boolean(extraInputSettings.extraInputRequired),
      extraInputDigitsOnly: Boolean(extraInputSettings.extraInputDigitsOnly),
      extraInputLettersOnly: extraInputSettings.extraInputDigitsOnly
        ? false
        : Boolean(extraInputSettings.extraInputLettersOnly),
      extraInputMinLength: extraInputLimitLength
        ? extraInputSettings.extraInputMinLength ?? null
        : null,
      extraInputMaxLength: extraInputLimitLength
        ? extraInputSettings.extraInputMaxLength ?? null
        : null,
    };
    const topicData = {
      nameRu: inputTextRu,
      nameBe: inputTextBy,
      nameEn: inputTextEn,
      itemNamePrefix: letter,
      branchIds,
      queueIds,
      startWork: startTime,
      endWork: endTime,
      ...mapItemIconFields(iconSettings),
      ...mapChildrenIconFields(childrenIconSettings),
      ...extraPayload,
    };
    if (startTime && endTime) {
      modalMode === ModalMode.Edit
        ? editTopic(topicData)
        : createTopic(topicData);
    }
  };

  const isBindingValid =
    selectedBranchIds.length > 0 && selectedQueueIds.length > 0;
  const showBindingError = isBindingTouched && !isBindingValid;

  return (
    <form className={classes.serviceForm}>
      <h2 className={modalBase.title}>Категория / услуга</h2>
      <div className={modalBase.scrollArea}>
        <div className={classes.serviceContent}>
          {(isLoadingCreatedTopic || isLoadingTopicEditing) && (
            <div className={classes.load}>
              <div className={classes.loadContent}>
                <p>Cохранение...</p>
              </div>
            </div>
          )}
          <div className={classes.card}>
            <div className={classes.firstRow}>
              <div className={classes.radioContainer}>
                <div className={classes.radio}>
                  <CustomRadio
                    id={"section"}
                    value={"section"}
                    checked={selectedRadioValue === "section"}
                    onChange={handleRadioChange}
                    label={"Категория"}
                    disabled={
                      modalMode === "edit" &&
                      modalDataFromTable.radioType === "service"
                    }
                  />
                </div>
                <div className={classes.radio}>
                  <CustomRadio
                    id={"service"}
                    value={"service"}
                    checked={selectedRadioValue === "service"}
                    onChange={handleRadioChange}
                    label={"Услуга"}
                    disabled={
                      modalMode === "edit" &&
                      modalDataFromTable.radioType === "section"
                    }
                  />
                </div>
              </div>
            </div>
            <h3 className={classes.sectionTitle}>Параметры</h3>
            <div className={classes.letterContainer}>
              <CustomTextField
                {...register("letter")}
                name="letter"
                value={watch("letter")}
                errorMessage={isSubmitted ? errors.letter?.message : undefined}
                labelName={"Буква при выдаче билета"}
              ></CustomTextField>
            </div>
            <h3 className={classes.sectionTitle}>Дополнительные данные</h3>
            <label className={classes.checkboxRow}>
              <input
                type="checkbox"
                checked={Boolean(extraInputSettings.extraInputEnabled)}
                onChange={(e) =>
                  setExtraInputSettings((prev) => ({
                    ...prev,
                    extraInputEnabled: e.target.checked,
                  }))
                }
              />
              Ввод дополнительных данных
            </label>
            {extraInputSettings.extraInputEnabled && (
              <div className={classes.extraInputBlock}>
                <CustomTextField
                  isTextarea
                  name="extraInputDescription"
                  value={extraInputSettings.extraInputDescription ?? ""}
                  onChange={(e) =>
                    setExtraInputSettings((prev) => ({
                      ...prev,
                      extraInputDescription: e.target.value,
                    }))
                  }
                  labelName="Описание"
                  errorMessage={extraInputError ?? undefined}
                />
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={Boolean(extraInputSettings.extraInputPrint)}
                    onChange={(e) =>
                      setExtraInputSettings((prev) => ({
                        ...prev,
                        extraInputPrint: e.target.checked,
                      }))
                    }
                  />
                  Печатать дополнительные данные на талоне
                </label>
                <div className={classes.validationTitle}>Валидация поля</div>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={Boolean(extraInputSettings.extraInputRequired)}
                    onChange={(e) =>
                      setExtraInputSettings((prev) => ({
                        ...prev,
                        extraInputRequired: e.target.checked,
                      }))
                    }
                  />
                  Обязательное поле
                </label>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={Boolean(extraInputSettings.extraInputDigitsOnly)}
                    onChange={(e) =>
                      setExtraInputSettings((prev) => ({
                        ...prev,
                        extraInputDigitsOnly: e.target.checked,
                        extraInputLettersOnly: e.target.checked
                          ? false
                          : prev.extraInputLettersOnly,
                      }))
                    }
                  />
                  Только цифры
                </label>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={Boolean(extraInputSettings.extraInputLettersOnly)}
                    disabled={Boolean(extraInputSettings.extraInputDigitsOnly)}
                    onChange={(e) =>
                      setExtraInputSettings((prev) => ({
                        ...prev,
                        extraInputLettersOnly: e.target.checked,
                      }))
                    }
                  />
                  Только буквы
                </label>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={extraInputLimitLength}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setExtraInputLimitLength(enabled);
                      if (!enabled) {
                        setExtraInputSettings((prev) => ({
                          ...prev,
                          extraInputMinLength: null,
                          extraInputMaxLength: null,
                        }));
                      }
                    }}
                  />
                  Ограничить длину
                </label>
                {extraInputLimitLength && (
                  <div className={classes.lengthRow}>
                    <label className={classes.lengthField}>
                      Мин.
                      <input
                        type="number"
                        min={1}
                        max={128}
                        value={extraInputSettings.extraInputMinLength ?? ""}
                        onChange={(e) =>
                          setExtraInputSettings((prev) => ({
                            ...prev,
                            extraInputMinLength: e.target.value
                              ? Number(e.target.value)
                              : null,
                          }))
                        }
                      />
                    </label>
                    <label className={classes.lengthField}>
                      Макс.
                      <input
                        type="number"
                        min={1}
                        max={128}
                        value={extraInputSettings.extraInputMaxLength ?? ""}
                        onChange={(e) =>
                          setExtraInputSettings((prev) => ({
                            ...prev,
                            extraInputMaxLength: e.target.value
                              ? Number(e.target.value)
                              : null,
                          }))
                        }
                      />
                    </label>
                  </div>
                )}
              </div>
            )}
            <h3 className={classes.sectionTitle}>Привязка</h3>
            <BranchQueueTreeSelect
              selectedBranchIds={selectedBranchIds}
              selectedQueueIds={selectedQueueIds}
              onBranchChange={(ids) => {
                selectedBranchIdsRef.current = ids;
                setSelectedBranchIds(ids);
                setIsBindingTouched(true);
              }}
              onQueueChange={(ids) => {
                selectedQueueIdsRef.current = ids;
                setSelectedQueueIds(ids);
                setIsBindingTouched(true);
              }}
              className={classes.queuesBlock}
              queueIdsFilter={queueIdsFilter}
            />
            {showBindingError && (
              <p className={classes.timeError}>
                Выберите привязку к филиалу и очереди
              </p>
            )}
            <h3 className={classes.sectionTitle}>График работы</h3>
            <div className={classes.timeContainer}>
              <div className={classes.startTime}>
                <TimePickerCustom
                  label={"Начало работы"}
                  time={startTime}
                  setTime={setStartTime}
                  modalDataFromTable={modalDataFromTable}
                  timeType={TimeMode.Start}
                />
                {checkStartTime() && (
                  <p className={classes.timeError}>Необходимо заполнить поле</p>
                )}
              </div>
              <div className={classes.endTime}>
                <TimePickerCustom
                  label={"Конец работы"}
                  time={endTime}
                  setTime={setEndTime}
                  modalDataFromTable={modalDataFromTable}
                  timeType={TimeMode.End}
                />
                {checkEndTime() && (
                  <p className={classes.timeError}>Необходимо заполнить поле</p>
                )}
              </div>
            </div>
          </div>
          <div className={classes.card}>
            <h3 className={classes.sectionTitle}>Наименования</h3>
            <div className={classes.nameRu}>
              <CustomTextField
                {...register("inputTextRu")}
                name="inputTextRu"
                value={watch("inputTextRu")}
                labelName={"Наименование RU"}
                errorMessage={
                  isSubmitted ? errors.inputTextRu?.message : undefined
                }
              />
            </div>
            <div className={classes.nameEn}>
              <CustomTextField
                {...register("inputTextEn")}
                name="inputTextEn"
                value={watch("inputTextEn")}
                labelName={"Наименование EN"}
                errorMessage={
                  isSubmitted ? errors.inputTextEn?.message : undefined
                }
              />
            </div>
            <div className={classes.nameBe}>
              <CustomTextField
                {...register("inputTextBy")}
                name="inputTextBy"
                value={watch("inputTextBy")}
                labelName={"Наименование BY"}
                errorMessage={
                  isSubmitted ? errors.inputTextBy?.message : undefined
                }
              />
            </div>
          </div>
          <div className={classes.card}>
            <h3 className={classes.sectionTitle}>Иконка</h3>
            <ItemIconEditor
              idPrefix="topic"
              value={iconSettings}
              onChange={setIconSettings}
              isUploading={isUploadTopicIconLoading}
              onUpload={(file) => uploadTopicIcon(file)}
            />
          </div>
          <div className={classes.card}>
            <h3 className={classes.sectionTitle}>Иконка для детского режима</h3>
            <ItemIconEditor
              idPrefix="topic-children"
              value={childrenIconSettings}
              onChange={setChildrenIconSettings}
              isUploading={isUploadTopicChildrenIconLoading}
              onUpload={(file) => uploadTopicChildrenIcon(file)}
            />
          </div>
        </div>
      </div>
      <div className={classNames(modalBase.footer, classes.buttonsContainer)}>
        <Button
          content={"отмена"}
          btnStyle={"white"}
          onClick={() => setActiveModal(false)}
        />
        <Button
          content={"сохранить"}
          btnStyle={"blue"}
          onClick={() => {
            setIsBindingTouched(true);
            selectedQueueIdsRef.current = selectedQueueIds;
            selectedBranchIdsRef.current = selectedBranchIds;
            handleSubmit(onSubmit)();
          }}
          isLoading={isLoadingCreatedTopic || isLoadingTopicEditing}
        />
      </div>
    </form>
  );
};
